import { AionCta } from "@/components/site/AionCta";
import { OG_ABOUT } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Trophy } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Raj Shamani Success Story — From Indore to 200+ Interviews";
const DESC =
  "How Raj Shamani went from a Marwari family business in Indore to selling snacks at 16, speaking at the UN, writing Build Don't Talk and building House of X.";
const URL = "https://rajshamani.life/raj-shamani-success-story";
const OG = OG_ABOUT;

const timeline = [
  {
    stage: "Indore, the family business",
    detail:
      "Raj grew up in a Marwari business family in Indore and studied at Daly College. Shamani Industries, the family manufacturing business, was the first place he saw how products, margins and distribution actually work.",
  },
  {
    stage: "Selling at 16",
    detail:
      "His first attempts at business started in his teens — small trading and selling experiments rather than a funded startup. The lesson he repeats from that period is distribution: a product only exists if someone can buy it.",
  },
  {
    stage: "Speaking on global stages",
    detail:
      "He began speaking at conferences and student events, eventually reaching 150+ stages across 25+ countries, including the United Nations. Speaking, not content, was the first thing that made him known outside Indore.",
  },
  {
    stage: "Figuring Out (2020 onwards)",
    detail:
      "The Figuring Out podcast turned a speaking audience into a compounding one. Long-form interviews with founders, investors, actors and public figures made him one of India's most-watched business interviewers.",
  },
  {
    stage: "Build, Don't Talk (2022)",
    detail:
      "His book argued the point his whole career makes: output beats credentials. It became a bestseller in India and remains the clearest summary of how he thinks about work.",
  },
  {
    stage: "House of X and investing",
    detail:
      "He co-founded House of X, a D2C venture studio, and became an active angel investor — moving from being an audience owner to being an operator and backer of brands.",
  },
];

const lessons = [
  {
    title: "Distribution before product",
    detail:
      "Almost every stage of his career started with an audience or a channel first, then a product built for it — the podcast before the book, the book before the venture studio.",
  },
  {
    title: "Publish before you feel ready",
    detail:
      "Build, Don't Talk is essentially an argument against waiting for permission, credentials or the perfect launch moment.",
  },
  {
    title: "Compound one format",
    detail:
      "He did not switch formats every year. Long-form interviews were repeated for hundreds of episodes until the format itself became the asset.",
  },
  {
    title: "Own more than attention",
    detail:
      "Media alone is fragile. Manufacturing, the venture studio and investments give the income base that content alone would not.",
  },
];

const faq = [
  {
    q: "What is Raj Shamani's success story in short?",
    a: "Raj Shamani grew up in a Marwari business family in Indore, started selling in his teens, built a speaking career across 150+ stages and 25+ countries, launched the Figuring Out podcast in 2020, published the bestseller Build, Don't Talk in 2022, and co-founded the D2C venture studio House of X while investing in early-stage Indian startups.",
  },
  {
    q: "How did Raj Shamani become famous?",
    a: "Public speaking made him known first, but the Figuring Out podcast made him mainstream. Long-form interviews with high-profile founders, investors and public figures gave him consistent reach that his book and ventures then built on.",
  },
  {
    q: "What was Raj Shamani's first business?",
    a: "His first commercial experience came through his family's manufacturing business, Shamani Industries, alongside small trading and selling ventures he ran as a teenager in Indore.",
  },
  {
    q: "What can founders learn from Raj Shamani?",
    a: "The recurring lessons are to build distribution before a polished product, publish before you feel qualified, repeat one format long enough for it to compound, and convert attention into businesses you actually own.",
  },
];

export const Route = createFileRoute("/raj-shamani-success-story")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani success story", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            jobTitle: "Entrepreneur, investor and speaker",
            url: "https://rajshamani.life/about",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: SuccessStoryPage,
});

function SuccessStoryPage() {
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Success Story" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Trophy size={14} /> Success story
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            The Raj Shamani <span className="text-gradient-gold">success story</span>
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Raj Shamani grew up in a Marwari business family in{" "}
            <strong className="text-foreground">Indore</strong>, started selling in his teens, built
            a speaking career across 150+ stages in 25+ countries, launched the{" "}
            <Link to="/raj-shamani-podcast" className="text-gold underline underline-offset-4">
              Figuring Out podcast
            </Link>{" "}
            in 2020, published the bestseller{" "}
            <Link to="/build-dont-talk" className="text-gold underline underline-offset-4">
              Build, Don&apos;t Talk
            </Link>{" "}
            in 2022, and co-founded the D2C venture studio{" "}
            <Link to="/house-of-x" className="text-gold underline underline-offset-4">
              House of X
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">The story, stage by stage</h2>
        <ol className="space-y-6">
          {timeline.map((t, i) => (
            <li key={t.stage} className="flex gap-5">
              <div className="shrink-0 h-8 w-8 rounded-full border border-gold/40 text-gold grid place-items-center text-sm font-semibold">
                {i + 1}
              </div>
              <div>
                <h3 className="font-bold mb-1">{t.stage}</h3>
                <p className="text-muted-foreground leading-relaxed">{t.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">What founders actually take from it</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {lessons.map((l) => (
            <div key={l.title} className="p-6 rounded-xl border border-border bg-card">
              <h3 className="font-bold mb-2">{l.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{l.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/raj-shamani-success-story" />
      <RelatedPages pages={["about", "book", "ventures", "podcast", "aion", "faq"]} />
    </div>
  );
}
